/**
 * Input Component — HMVMReviewer Design System Phase 1
 * 
 * Features:
 * - Variants: text, password, email, number, search
 * - States: default, focus, disabled, error
 * - Label and error message support
 * - Full accessibility (ARIA, required indicator)
 * - Uses semantic theme tokens
 * - Square geometry (border-radius: 0)
 */

import { InputHTMLAttributes } from 'react';

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  helperText?: string;
  fullWidth?: boolean;
}

export function Input({
  label,
  error,
  helperText,
  fullWidth = false,
  className = '',
  id,
  required,
  disabled,
  ...props
}: InputProps) {
  const inputId = id || `input-${Math.random().toString(36).slice(2, 11)}`;
  const errorId = error ? `${inputId}-error` : undefined;
  const helperId = helperText ? `${inputId}-helper` : undefined;

  return (
    <div className={`flex flex-col gap-1.5 ${fullWidth ? 'w-full' : ''}`}>
      {label && (
        <label
          htmlFor={inputId}
          className="text-[var(--reviewer-text)] text-[var(--reviewer-text-sm)] font-medium font-mono"
        >
          {label}
          {required && (
            <span className="text-[var(--reviewer-danger)] ml-1" aria-label="required">
              *
            </span>
          )}
        </label>
      )}
      
      <input
        id={inputId}
        disabled={disabled}
        required={required}
        aria-invalid={error ? 'true' : 'false'}
        aria-describedby={[errorId, helperId].filter(Boolean).join(' ') || undefined}
        className={`
          w-full px-3 py-2
          bg-[var(--reviewer-input-bg)]
          border border-[var(--reviewer-input-border)]
          text-[var(--reviewer-text)]
          font-mono text-[var(--reviewer-text-base)]
          transition-all duration-150
          focus:outline-none
          focus:border-[var(--reviewer-input-focus-border)]
          focus:ring-2 focus:ring-[var(--reviewer-input-focus-ring)]
          disabled:opacity-50 disabled:cursor-not-allowed
          ${error ? 'border-[var(--reviewer-danger-border)] focus:border-[var(--reviewer-danger)] focus:ring-[var(--reviewer-danger-soft)]' : ''}
          ${className}
        `}
        {...props}
      />
      
      {error && (
        <p
          id={errorId}
          className="text-[var(--reviewer-danger)] text-[var(--reviewer-text-xs)] font-mono"
          role="alert"
        >
          {error}
        </p>
      )}
      
      {helperText && !error && (
        <p
          id={helperId}
          className="text-[var(--reviewer-text-secondary)] text-[var(--reviewer-text-xs)] font-mono"
        >
          {helperText}
        </p>
      )}
    </div>
  );
}
